import { RecipientDetailsModel } from "@/Appointments/Models/Appointment/RecipientDetailsModel";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { LabSearchAutocomplete } from "@/Common/Components/Search/LabSearchAutocomplete";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { FaxDirectorySearchResultItem } from "@/ContactDirectory/Components/Search/FaxDirectorySearchResultItem";
import { RecipientContactDirectorySearchAndModify } from "@/ContactDirectory/Components/Search/RecipientContactDirectorySearchAndModify";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";

import styles from "./RecipientDetails.module.scss";

interface IRecipientDetailsProps {
    isEditMode: boolean;
    recipientDetails: RecipientDetailsModel;
    onChange: (recipientDetails: RecipientDetailsModel) => void;
}

function RecipientDetails(props: IRecipientDetailsProps): JSX.Element {
    const { getText } = useTranslation();
    const phoneValidationRule = new ValidationHelper().getPhoneNumberValidationRule(cacheStore.SiteCache[0]?.Model?.Adresses[0]?.CountryLookupId, null);
    const validationRegex = phoneValidationRule.ValidationRegexPattern;
    const phoneMask = phoneValidationRule.EditMaskPattern;

    function renderRow(label: string, value: string, element: JSX.Element): JSX.Element {
        return (
            <div className={styles.row}>
                <div className={styles.label}>
                    <label>{label}</label>
                </div>
                <div>
                    {props.isEditMode && element}
                    {!props.isEditMode && (<span> {value} </span>)}
                </div>
            </div>
        );
    }

    function isNumberValid(phoneNumber: string): boolean {
        const regexp = new RegExp(validationRegex, "i");
        return ValidationHelper.isExactMatch(phoneNumber, regexp);
    }

    return (
        <div className={styles.recipientDetails}>

            <FeatureFlagControl
                flag={cacheStore.ClinicSettings.EnableContactDirectory}
                activeFeature={(
                    <RecipientContactDirectorySearchAndModify
                        headerLabel={getText("BillRecipient")}
                        fieldLabel={getText("RecipientLabel")}
                        isValid={!!props.recipientDetails.RecipientName}
                        onContactSelect={(selection: FaxDirectorySearchResultItem) => {
                            const contact = selection as IDirectoryEntity;

                            props.onChange({ ...props.recipientDetails,
                                RecipientName: contact.getDisplayName(),
                                RecipientAddress: contact.getDirectoryAddressForDisplay(),
                                RecipientPhoneNumber: contact.getRawPhoneNumber() ?? null,
                                RecipientFaxNumber: contact.getRawFaxNumber() ?? null
                            });
                        }}
                        text={props.recipientDetails.RecipientName}
                        onTextChangedFunction={recipientName => props.onChange({ ...props.recipientDetails, RecipientName: recipientName })}
                    />

                )}
                fallbackFeature={(
                    renderRow(
                        getText("RecipientLabel"),
                        props.recipientDetails?.RecipientName,
                        <LabSearchAutocomplete
                            isRequired
                            componentId="recipient"
                            onItemSelectCallback={lab => {
                                props.onChange({ ...props.recipientDetails,
                                    RecipientName: lab?.Model?.Name,
                                    RecipientAddress: lab?.Model?.Address,
                                    RecipientPhoneNumber: lab?.Model?.Phone,
                                    RecipientFaxNumber: lab?.Model?.Fax
                                });
                            }}
                            defaultText={props.recipientDetails?.RecipientName ?? ""}
                            onTextChangedFunction={(value: string) => {
                                props.onChange({ ...props.recipientDetails, RecipientName: value });
                            }}
                            maxLength={250}
                        />
                    )
                )}
            />

            {renderRow(
                getText("AddressLabel"),
                props.recipientDetails?.RecipientAddress,
                <TextboxControl
                    onChange={(value: string) => {
                        props.onChange({ ...props.recipientDetails, RecipientAddress: value });
                    }}
                    className="form-control"
                    value={props.recipientDetails?.RecipientAddress ?? ""}
                    id="address"
                    maxLength={300}
                />
            )}
            {renderRow(
                getText("PhoneNumberLabel"),
                props.recipientDetails?.RecipientPhoneNumber,
                <TextboxMaskedControl
                    allowEmpty
                    isValid={isNumberValid(props.recipientDetails?.RecipientPhoneNumber)}
                    id="phone-number"
                    value={props.recipientDetails?.RecipientPhoneNumber ?? ""}
                    mask={phoneMask}
                    onChangeFunction={value => props.onChange({ ...props.recipientDetails, RecipientPhoneNumber: value })}
                />
            )}
            {renderRow(
                getText("FaxNumberLabel"),
                props.recipientDetails?.RecipientFaxNumber,
                <TextboxMaskedControl
                    allowEmpty={false}
                    isValid={isNumberValid(props.recipientDetails?.RecipientFaxNumber)}
                    id="fax-number"
                    value={props.recipientDetails?.RecipientFaxNumber ?? ""}
                    mask={phoneMask}
                    onChangeFunction={value => props.onChange({ ...props.recipientDetails, RecipientFaxNumber: value })}
                />
            )}
        </div>
    );
}

export { RecipientDetails };
